import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import qs from 'query-string';
import { isTimeInterval } from '../ProductCard/ProductCard';

// Components
import Heading from '../Heading';
import Amount from '../Amount';
import Button from '../Button';
import SizeSelector from '../SizeSelector';
import Modal from '../Modal';
import AlcoholWarning from './AlcoholWarning';
import MetaOption from '../ProductCard/MetaOption';
import NavBar from '../NavBar';
import ProductCard from '../ProductCard';

// Router
import { getRoute, paths } from '../../entry/routes';

// State
import { getSizes, getVariation } from '../../state/modules/catalog/utils';

// Utils
import {
  isAlcohol,
  isHalal,
  isHit,
  isHot,
  isNew,
  isPizza,
  isVegan,
  isVegeterian,
} from '../../utils/catalog';

// Styles
import styles from './ProductCardCompact.styl';
import { unusedBonusesSum as unusedBonusesSumSelector } from '../../state/modules/user/selectors';
import useGiftsSlider from '../GiftsSlider/useGiftsSlider';
import { removeNumbers } from '../../utils/validators';
import EspecialButton from '../ProductCard/EspecialButton';
import CharacteristicsCompact from '../ProductCard/CharacteristicsCompact';

const cx = classNames.bind(styles);

export default function ProductCardCompact(props) {
  const {
    good,
    className,
    stockRole,
    addToCart,
    removeIngredientAction,
    cancelRemovedIngredientsAction,
    type,
    sailplayGifts,
    setIsOpenModalBonus,
    setIsOpenModalAuth,
    isReward = false,
    rewardIndex = null,
  } = props;

  const { id, variations, alias, name, data, types, good_type, category } = good;

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { product } = qs.parse(location.search, { parseNumbers: true });
  const [currentTime, setCurrentTime] = useState(
    new Date().getHours() * 60 + new Date().getMinutes(),
  );

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentTime(new Date().getHours() * 60 + new Date().getMinutes());
    }, 1000 * 60);
    return () => {
      clearInterval(timerId);
    };
  }, []);

  const [isAdding, setIsAdding] = useState(false);
  const [size, setSize] = useState(
    [...variations].sort((a, b) => {
      if (a.size?.value < b.size?.value) {
        return -1;
      }
      if (a.size?.value > b.size?.value) {
        return 1;
      }

      return 0;
    })[0]?.size?.value,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingPoints, setIsLoadingPoints] = useState(false);
  const { list, onAddClick } = useGiftsSlider();

  const addPointProductHandler = async () => {
    await setIsLoadingPoints(true);
    if (pointPrice) {
      !isAuthenticated && setIsOpenModalAuth(true);

      pointPrice > unusedBonusesSum && isAuthenticated && setIsOpenModalBonus(true);
      const gift = list.find(item => variation.id === item.item.id);
      if (isAuthenticated && pointPrice <= unusedBonusesSum) {
        await onAddClick(gift);
      }
      await setIsLoadingPoints(false);
    }
  };
  const isVariationInCurrentTime = variations.filter(item => {
    const enabledTimes = item.enabled_times;
    return isTimeInterval(enabledTimes, currentTime);
  });

  // вариация исходя из выбранного размера
  const variation = getVariation(isVariationInCurrentTime, size);

  const categoriesAliases = useSelector(state => state.catalog.products.categoriesAliases);
  const sailplayType = useSelector(state => state.city?.userCity?.sailplay?.type);
  const isDisabledLoyalty = useMemo(() => sailplayType !== 'none', [sailplayType]);
  const isGift = sailplayGifts?.some(item => item.item.id === variation.id);
  const isAuthenticated = useSelector(state => state.user.isAuthenticated);
  const pointPrice = sailplayGifts?.find(item => item.item.id === variation.id)?.points;
  const unusedBonusesSum = useSelector(unusedBonusesSumSelector);

  // доступные размеры пиццы
  const sizes = getSizes(isVariationInCurrentTime);

  const ingredients = variation.include_ingredients || [];
  const isMetaOptionsExist = isHot(types) || isHalal(types) || isVegeterian(types);

  const badges = useMemo(() => {
    const result = [
      isVegan(types) && (
        <div className={cx('ProductCardCompact__badge', 'ProductCardCompact__badge_vegan')}>
          {intl.formatMessage({ id: 'vegan' })}
        </div>
      ),
      !isVegan(types) && isNew(types) && <div className={cx(`ProductCardCompact__badge`)}>New</div>,
      !isVegan(types) && !isNew(types) && isHit(types) && (
        <div className={cx(`ProductCardCompact__badge`)}>Hit</div>
      ),
    ].filter(Boolean);

    return result.length > 0 ? result : null;
  }, [types]);

  const openModalForPizza = () => {
    if (isPizza(variation) && (isVariationInCurrentTime.length > 1 || ingredients.length > 0)) {
      if (stockRole) {
        setIsModalOpen(true);
      } else {
        history.push({ search: qs.stringify({ product: id }) });
      }
    }
  };

  const hideModal = () => {
    if (stockRole) {
      setIsModalOpen(false);
    } else {
      history.push({ search: null });
    }
  };

  const changeSize = newSize => {
    setSize(newSize);
  };

  const goToComboboxPage = () => {
    history.push(getRoute(paths.comboboxAlias, removeNumbers(alias)));
  };

  const handleOnAddClick = async () => {
    if (isPizza(variation) && (isVariationInCurrentTime.length > 1 || ingredients.length > 0)) {
      if (stockRole) {
        setIsModalOpen(true);
      } else {
        history.push({ search: qs.stringify({ product: id }) });
      }
      return;
    }

    setIsAdding(true);
    await addToCart(variation);
    setIsAdding(false);
  };

  const addToCartWithModalHide = async selectedVariation => {
    await addToCart(selectedVariation);
    hideModal();
  };

  useEffect(() => {
    setIsModalOpen(product === id);
  }, [location.search, id]);

  const prefix =
    !isPizza(variation) || isVariationInCurrentTime.length === 1
      ? ''
      : intl.formatMessage({ id: 'amount.prefix' });
  const isExcluding = Boolean(variation.extra);
  const pic = variation?.image_mobile ? variation?.image_mobile : variation?.image_nutrition_webp;

  return (
    <div
      className={cx('ProductCardCompact', className)}
      style={isVariationInCurrentTime.length === 0 ? { display: 'none' } : { display: 'flex' }}
    >
      <div className={cx('ProductCardCompact__addContainer')} onClick={openModalForPizza}>
        <picture
          className={cx('ProductCardCompact__picture', {
            ProductCardCompact__picture_pizza: isPizza(variation),
          })}
        >
          {badges}
          <source srcSet={pic} type="image/webp" />
          <LazyLoadImage
            className={cx('ProductCardCompact__image')}
            alt={`${categoriesAliases[category]} ${name}`}
            src={pic}
            threshold={300}
          />
        </picture>
        <div className={cx('ProductCardCompact__content')}>
          <div className={cx('ProductCardCompact__characteristics')}>
            <Heading className={cx('ProductCardCompact__name')} level={3}>
              {name}
            </Heading>
            <div className={cx('ProductCardCompact__info')}>
              <CharacteristicsCompact characteristics={variation?.characteristics} />
            </div>
          </div>
          {Boolean(data) && <div className={cx('ProductCardCompact__description')}>{data}</div>}
          {isMetaOptionsExist && (
            <>
              {isHot(types) && (
                <MetaOption className={cx('ProductCardCompact__option')} type="hot" />
              )}
              {isHalal(types) && (
                <MetaOption className={cx('ProductCardCompact__option')} type="halal" />
              )}
              {isVegeterian(types) && (
                <MetaOption className={cx('ProductCardCompact__option')} type="vegetarian" />
              )}
            </>
          )}
          {!isPizza(variation) && sizes.length > 0 && (
            <SizeSelector
              className={cx('ProductCardCompact__sizes')}
              sizes={sizes}
              onChange={changeSize}
              currentSize={size}
              specialStylesMode
            />
          )}
          {!isExcluding ? (
            <div className={cx('ProductCardCompact__footer')}>
              {type !== 'gift' && (
                <Amount
                  className={cx('ProductCardCompact__price')}
                  value={variation.old_price || variation.price}
                  newValue={variation.price}
                  size="s"
                  prefix={prefix}
                  specialStylesMode
                />
              )}
              {isDisabledLoyalty && isGift && !isPizza(variation) && (
                <button
                  className={cx('ProductCardCompact__points', {
                    ProductCardCompact__points_disabled: pointPrice > unusedBonusesSum,
                  })}
                  onClick={addPointProductHandler}
                >
                  {!isLoadingPoints ? (
                    `${pointPrice} ${intl.formatMessage({ id: 'bonus' })}`
                  ) : (
                    <span className={cx('ProductCardCompact__spinner')} />
                  )}
                </button>
              )}
              {good_type === 'combo' ? (
                <Button
                  onClick={goToComboboxPage}
                  className={cx('ProductCardCompact__button')}
                  secondary
                  small
                >
                  {intl.formatMessage({ id: 'combobox.card.button' })}
                </Button>
              ) : (
                <Button
                  className={cx('ProductCardCompact__button')}
                  onClick={handleOnAddClick}
                  isLoading={isAdding}
                  secondary
                  small
                >
                  {intl.formatMessage({
                    id:
                      isVariationInCurrentTime.length === 1 && !ingredients.length
                        ? 'button.inCart'
                        : 'button.choose',
                  })}
                </Button>
              )}
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                marginTop: '8px',
              }}
            >
              <EspecialButton
                className={cx('ProductCard__button')}
                onAddToCart={handleOnAddClick}
                isLoading={isAdding}
                value={variation.old_price || variation.price}
                newValue={variation.price}
                currentSize={size}
                sizes={sizes}
                variation={variation}
              />
            </div>
          )}
        </div>
        {isAlcohol(types) && <AlcoholWarning />}
      </div>
      <Modal isOpen={isModalOpen} onClose={hideModal}>
        <NavBar
          view="secondary"
          title={intl.formatMessage({ id: 'button.back' })}
          onBack={hideModal}
        />
        <ProductCard
          good={good}
          sailplayGifts={sailplayGifts}
          stockRole={stockRole}
          addToCart={addToCartWithModalHide}
          removeIngredientAction={removeIngredientAction}
          cancelRemovedIngredientsAction={cancelRemovedIngredientsAction}
          type={type}
          compact
          hideModal={hideModal}
          setIsOpenModalBonus={setIsOpenModalBonus}
          setIsOpenModalAuth={setIsOpenModalAuth}
          isReward
          rewardIndex
        />
      </Modal>
    </div>
  );
}

ProductCardCompact.defaultProps = {
  className: '',
  stockRole: '',
  type: 'good',
};

ProductCardCompact.propTypes = {
  className: PropTypes.string,
  stockRole: PropTypes.string,
  good: PropTypes.object.isRequired,
  addToCart: PropTypes.func.isRequired,
  removeIngredientAction: PropTypes.func.isRequired,
  cancelRemovedIngredientsAction: PropTypes.func.isRequired,
  type: PropTypes.string,
};
